// Touch/narrow galleries must show the base image, not a stale hover zoom clone.
(function () {
  const id = 'dalmivik-touch-zoom-clone-guard';
  if (document.getElementById(id)) return;
  const style = document.createElement('style');
  style.id = id;
  style.textContent = '@media (max-width: 767px), (hover: none), (pointer: coarse) { #proFeaturedImageZoom img.zoomImg { display: none !important; } }';
  document.head.appendChild(style);
})();

class ProductVariantDropdown extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
  }

  connectedCallback() {
    const root = this.closest('#content_quickview');
    if (!root) {
      if (this.tagName === 'PRODUCT-VARIANT-SELECTS-STICK') return;
      const previous = this.initialMediaConnection;
      if (previous) {
        window.removeEventListener('load', previous.sync);
        previous.cleanup();
      }
      const connection = { root: this.domRoot(), productId: this.dataset.productId, intentSeen: false };
      this.initialMediaConnection = connection;
      this.initialMediaIntentSeen = false;
      connection.current = () => this.isConnected && this.initialMediaConnection === connection &&
        this.domRoot() === connection.root && this.dataset.productId === connection.productId;
      const onThumbIntent = event => {
        if (!connection.current()) return;
        const thumb = event.target.closest?.('#productThumbs .thumbItem');
        const gallery = this.scopeOne('.proFeaturedImage');
        if (thumb && connection.root.contains(thumb) && gallery?.classList.contains('slick-initialized')) {
          connection.intentSeen = true;
          this.initialMediaIntentSeen = true;
        }
      };
      connection.cleanup = () => {
        connection.root.removeEventListener('click', onThumbIntent, true);
        document.removeEventListener('DOMContentLoaded', connection.ready);
        const jq = window.jQuery;
        if (connection.waitGallery && typeof jq === 'function') jq(connection.waitGallery).off('init.dalmivikInitialMedia', connection.onInit);
        window.clearTimeout(connection.waitTimer);
      };
      connection.root.addEventListener('click', onThumbIntent, true);
      connection.sync = this.initialMediaSync = () => {
        if (!connection.current() || this.variantRequestVersion != null) {
          connection.cleanup();
          return;
        }
        try {
          this.updateOptions();
          this.updateMasterId();
          const requestedVariantId = new URLSearchParams(window.location.search).get('variant');
          Promise.resolve(connection.intentSeen || requestedVariantId !== String(this.currentVariant?.id) ? undefined : this.updateMedia(connection))
            .catch(() => { if (connection.current()) this.dataset.initialMediaSync = 'failed'; })
            .finally(connection.cleanup);
        } catch (_) {
          if (connection.current()) this.dataset.initialMediaSync = 'failed';
          connection.cleanup();
        }
      };
      // Start with DOM/owned Slick readiness; unrelated page loads must not gate the selected image.
      connection.ready = () => {
        if (!connection.current() || this.variantRequestVersion != null) { connection.cleanup(); return; }
        const gallery = this.scopeOne('.proFeaturedImage');
        const jq = window.jQuery;
        if (gallery?.classList.contains('slick-initialized')) { queueMicrotask(connection.sync); return; }
        if (!gallery || typeof jq !== 'function') { this.dataset.initialMediaSync = 'failed'; connection.cleanup(); return; }
        connection.waitGallery = gallery;
        connection.onInit = () => queueMicrotask(connection.sync);
        jq(gallery).one('init.dalmivikInitialMedia', connection.onInit);
        connection.waitTimer = window.setTimeout(() => {
          if (connection.current() && this.variantRequestVersion == null) this.dataset.initialMediaSync = 'failed';
          connection.cleanup();
        }, 1500);
      };
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', connection.ready, { once: true });
      else connection.ready();
      return;
    }
    this.quickviewGuard = event => {
      const blocked = this.guardQuickviewPurchase();
      const target = event?.target;
      const form = target?.closest?.('form');
      const owned = form && (form === this.closest('form') || this.selectionForms().includes(form));
      if (blocked && owned && (event?.type === 'submit' || target?.closest?.('[name="add"], [type="submit"], .shopify-payment-button, [data-commercial-dynamic-checkout], .product-form__item--checkout, shopify-accelerated-checkout'))) {
        event.preventDefault(); event.stopImmediatePropagation();
      }
    };
    this.quickviewRoot = root;
    root.addEventListener('submit', this.quickviewGuard, true);
    root.addEventListener('click', this.quickviewGuard, true);
    this.quickviewObserver = new MutationObserver(() => this.guardQuickviewPurchase());
    this.quickviewObserver.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['id', 'data-product-id', 'disabled', 'value', 'hidden', 'inert'] });
    this.guardQuickviewPurchase();
  }

  disconnectedCallback() {
    window.removeEventListener('load', this.initialMediaSync);
    this.initialMediaConnection?.cleanup();
    this.initialMediaConnection = null;
    this.initialMediaIntentSeen = false;
    this.quickviewObserver?.disconnect();
    this.quickviewRoot?.removeEventListener('submit', this.quickviewGuard, true);
    this.quickviewRoot?.removeEventListener('click', this.quickviewGuard, true);
  }

  static get observedAttributes() { return ['data-product-id']; }

  attributeChangedCallback() {
    if (this.isConnected) {
      const connection = this.initialMediaConnection;
      if (connection && connection.productId !== this.dataset.productId) this.connectedCallback();
      this.guardQuickviewPurchase();
    }
  }

  guardQuickviewPurchase() {
    const root = this.closest('#content_quickview');
    if (!root) return false;
    // The loader remaps component and form IDs in separate statements. Retain
    // the actual ancestor form during that interval; never search another PDP.
    const owner = this.closest('form');
    const forms = new Set(this.selectionForms());
    if (owner && owner.closest('#content_quickview') === root) forms.add(owner);
    let blocked = false;
    forms.forEach(form => {
      const maps = form.querySelectorAll('[data-commercial-availability-map]');
      let validMap = false;
      try {
        const map = maps.length === 1 && JSON.parse(maps[0].textContent);
        validMap = !!map && typeof map === 'object' && !Array.isArray(map) && Object.keys(map).length > 0 && Object.values(map).every(row => row && typeof row.commercialAllowed === 'boolean');
      } catch (_) { validMap = false; }
      if (validMap) return;
      blocked = true;
      form.querySelectorAll('input[name="id"]').forEach(input => {
        if (input.value) input.value = '';
        if (input.hasAttribute('value')) input.removeAttribute('value');
        if (!input.disabled) input.disabled = true;
      });
      form.querySelectorAll('[name="add"], button[type="submit"], input[type="submit"]').forEach(button => {
        if (!button.disabled) button.disabled = true;
        if (button.getAttribute('aria-disabled') !== 'true') button.setAttribute('aria-disabled', 'true');
      });
      form.querySelectorAll('[data-commercial-dynamic-checkout], .product-form__item--checkout, .shopify-payment-button, shopify-accelerated-checkout').forEach(checkout => {
        if (!checkout.hidden) checkout.hidden = true;
        if (!checkout.inert) checkout.inert = true;
        if (checkout.getAttribute('aria-hidden') !== 'true') checkout.setAttribute('aria-hidden', 'true');
      });
    });
    return blocked;
  }

  onVariantChange() {
    if (this.guardQuickviewPurchase()) return;
    this.updateOptions();
    this.updateMasterId();
    this.synchronizeSelection();
    this.toggleAddButton(true, '', false);
    this.updatePickupAvailability();
    this.updateVariantStatuses();
    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateVariantInput();
      this.renderProductInfo();
      if (document.querySelectorAll('.template-product').length && !this.closest('#content_quickview')) {
        this.updateURL();
      }
    }
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options.map((option, index) => {
        return this.options[index] === option;
      }).includes(false);
    });
  }

  featuredMedia(mediaId) {
    // Main slides and thumbnails legitimately share data-media-id. Resolve the role,
    // not the first ID match; canonical duplicate slides remain fail-closed.
    const galleries = this.scopeAll('.proFeaturedImage');
    if (galleries.length !== 1) return null;
    const gallery = galleries[0];
    const matches = this.scopeAll('.proFeaturedImage .item[data-media-id]').filter(node =>
      node.closest('.proFeaturedImage') === gallery && !node.closest('.slick-cloned') &&
      node.getAttribute('data-media-id') === `${this.dataset.productId}-${mediaId}`);
    return matches.length === 1 ? matches[0] : null;
  }

  updateMedia(initialConnection = null) {
    if (initialConnection && (!initialConnection.current() || initialConnection.intentSeen)) return;
    if (!this.currentVariant || !this.currentVariant?.featured_media) return;
    const newMedia = this.featuredMedia(this.currentVariant.featured_media.id);
    if (!newMedia) return;
    const parent = newMedia.parentElement;
    const gallery = newMedia.closest('.proFeaturedImage');
    const itemact = Array.from(gallery.querySelectorAll('.item'));
    for (var i = 0; i < itemact.length; i++) {
      itemact[i].classList.remove('act')
    }
    newMedia.classList.add('act');
    // Commit after async section validation; the theme's 300ms label timer may
    // already have fired. Use the existing, owned Slick instance, never global.
    if (gallery.classList.contains('slick-initialized')) {
      const jq = window.jQuery;
      if (typeof jq !== 'function' || typeof jq.fn.slick !== 'function') throw new Error('MEDIA_RUNTIME_MISSING');
      const slick = jq(gallery).slick('getSlick');
      const index = Array.from(slick.$slides).indexOf(newMedia);
      if (index < 0) throw new Error('MEDIA_SLIDE_OWNERSHIP');
      const version = this.variantRequestVersion;
      const variantId = this.currentVariant.id;
      const activate = () => {
        if (initialConnection && (!initialConnection.current() || initialConnection.intentSeen)) return;
        if (!gallery.isConnected || jq(gallery).slick('getSlick') !== slick || this.featuredMedia(this.currentVariant.featured_media.id) !== newMedia) throw new Error('MEDIA_RUNTIME_REPLACED');
        jq(gallery).slick('slickGoTo', index, true);
        if (slick.currentSlide !== index || !newMedia.classList.contains('slick-active')) throw new Error('MEDIA_SLIDE_NOT_ACTIVE');
      };
      // Native Slick drops slickGoTo while animating. Its afterChange fires
      // before animating is cleared; wait boundedly for that same instance.
      if (slick.animating) return new Promise((resolve, reject) => {
        const deadline = performance.now() + 1500;
        const settle = () => {
          try {
            if (initialConnection && (!initialConnection.current() || initialConnection.intentSeen)) { resolve(); return; }
            if (!this.responseIsCurrent(version, variantId)) throw new Error('MEDIA_SELECTION_SUPERSEDED');
            if (!gallery.isConnected || jq(gallery).slick('getSlick') !== slick) throw new Error('MEDIA_RUNTIME_REPLACED');
            if (performance.now() >= deadline) throw new Error('MEDIA_SETTLE_TIMEOUT');
            if (slick.animating) { window.setTimeout(settle, 25); return; }
            activate(); resolve();
          } catch (error) { reject(error); }
        };
        window.setTimeout(settle, 0);
      });
      activate();
    }
    const scrollList = parent.closest?.('.slick-list');
    const scrollHost = scrollList?.closest('.proFeaturedImage') === gallery ? scrollList : parent;
    window.setTimeout(() => { if (!initialConnection || initialConnection.current()) scrollHost.scroll(0, 0) });
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({ }, '', `${this.dataset.productUrl}?variant=${this.currentVariant.id}`);
  }

  domRoot() {
    return this.closest('#content_quickview') || this.closest('.shopify-section') || document;
  }

  scopeAll(selector) {
    const scope = this.selectionScope();
    return Array.from(this.domRoot().querySelectorAll(selector)).filter(node => (node.closest('#content_quickview') || document) === scope);
  }

  scopeOne(selector) {
    const nodes = this.scopeAll(selector);
    return nodes.length === 1 ? nodes[0] : null;
  }

  scopeId(id) {
    const nodes = this.scopeAll('[id]').filter(node => node.getAttribute('id') === id);
    return nodes.length === 1 ? nodes[0] : null;
  }

  priceContainer(nodes) {
    // The theme repeats the container ID on its nested semantic money span.
    const containers = nodes.filter(node => node.matches('div.product-single__price'));
    if (containers.length !== 1) return null;
    const container = containers[0];
    const nested = nodes.filter(node => node !== container);
    if (nested.length > 1 || nested.some(node => !container.contains(node) || !node.matches('span.money[itemprop="price"]'))) return null;
    return container;
  }

  ownedPrice(id) {
    return this.priceContainer(this.scopeAll('[id]').filter(node => node.getAttribute('id') === id));
  }

  selectedDisplayCurrency() {
    const base = typeof shopCurrency === 'string' ? shopCurrency : null;
    if (!base) throw new Error('SHOP_CURRENCY_MISSING');
    const menus = Array.from(document.querySelectorAll('[id="currencies"]'));
    const selected = menus.map(menu => {
      const nodes = menu.querySelectorAll('span.selected[data-currency]');
      if (nodes.length !== 1) throw new Error('CURRENCY_SELECTION_AMBIGUOUS');
      return nodes[0].getAttribute('data-currency');
    });
    const currency = selected[0] || base;
    if (selected.some(value => value !== currency)) throw new Error('CURRENCY_MENUS_DISAGREE');
    if (typeof Currency === 'undefined') {
      if (currency !== base) throw new Error('CURRENCY_RUNTIME_MISSING');
      return currency;
    }
    if (Currency.currentCurrency && Currency.currentCurrency !== currency) throw new Error('CURRENCY_RUNTIME_DISAGREES');
    if (typeof Currency.convertAll !== 'function' || typeof window.jQuery !== 'function' ||
        !Number.isFinite(Currency.rates?.[base]) || Currency.rates[base] <= 0 ||
        !Number.isFinite(Currency.rates?.[currency]) || Currency.rates[currency] <= 0 ||
        !Currency.moneyFormats?.[base]?.[Currency.format] || !Currency.moneyFormats?.[currency]?.[Currency.format]) throw new Error('CURRENCY_CONTRACT_MISSING');
    return currency;
  }

  displayPrice(baseHtml, currency) {
    const detached = document.createElement('div');
    detached.innerHTML = baseHtml;
    const money = Array.from(detached.querySelectorAll('span.money'));
    if (typeof Currency !== 'undefined') {
      // Reuse the site's converter on detached owned prices only; no global DOM conversion.
      money.forEach(node => {
        Array.from(node.attributes).filter(attr => /^data-currency(?:-[a-z]{3})?$/.test(attr.name)).forEach(attr => node.removeAttribute(attr.name));
        node.setAttribute(`data-currency-${shopCurrency}`, node.innerHTML);
        // Seed supported display caches from the same base amount, using native FX/format APIs.
        // This matches convertAll's source-amount parser, without chained display rounding.
        const format = Currency.moneyFormats[shopCurrency][Currency.format];
        const amount = parseInt(node.innerHTML.replace(/[^0-9]/g, ''), 10) * (format.includes('amount_no_decimals') ? 100 : ['JOD', 'KWD', 'BHD'].includes(shopCurrency) ? 0.1 : 1);
        if (!Number.isFinite(amount)) throw new Error('CURRENCY_BASE_AMOUNT_INVALID');
        const currencies = new Set([currency, ...Array.from(document.querySelectorAll('[id="currencies"] span[data-currency]'), item => item.getAttribute('data-currency'))]);
        currencies.forEach(code => {
          if (!Number.isFinite(Currency.rates?.[code]) || Currency.rates[code] <= 0 || !Currency.moneyFormats?.[code]?.[Currency.format]) throw new Error('CURRENCY_CACHE_CONTRACT_MISSING');
          if (code !== shopCurrency) node.setAttribute(`data-currency-${code}`, Currency.formatMoney(Currency.convert(amount, shopCurrency, code), Currency.moneyFormats[code][Currency.format]));
        });
      });
      Currency.convertAll(shopCurrency, currency, window.jQuery(money));
      if (money.some(node => node.getAttribute('data-currency') !== currency || !node.textContent.trim())) throw new Error('CURRENCY_CONVERSION_UNPROVEN');
    } else if (currency !== shopCurrency) throw new Error('CURRENCY_RUNTIME_MISSING');
    return detached.innerHTML;
  }

  normalizedPrice(html) {
    const detached = document.createElement('div'); detached.innerHTML = html;
    detached.querySelectorAll('span.money').forEach(node => Array.from(node.attributes)
      .filter(attr => /^data-currency(?:-[a-z]{3})?$/.test(attr.name))
      .forEach(attr => node.removeAttribute(attr.name)));
    return detached.innerHTML;
  }

  committedPricesMatch() {
    const currency = this.selectedDisplayCurrency();
    return !!this.priceCommit?.length && this.priceCommit.every(row =>
      this.normalizedPrice(row.node.innerHTML) === this.normalizedPrice(this.displayPrice(row.baseHtml, currency)));
  }

  settleScopedPurchase() {
    this.selectionForms().forEach(form => {
      const input = form.querySelector('input[name="id"]');
      let allowed = false;
      try {
        const map = JSON.parse(form.querySelector('[data-commercial-availability-map]')?.textContent || '{}');
        allowed = !!this.currentVariant && this.renderedVersion === this.variantRequestVersion && this.committedPricesMatch() && !input.disabled && input.value === String(this.currentVariant.id) && map[input.value]?.commercialAllowed === true;
      } catch (_) { allowed = false; }
      const button = form.querySelector('[name="add"]');
      if (button) {
        button.disabled = !allowed; button.setAttribute('aria-disabled', String(!allowed));
        if (allowed) {
          const label = button.querySelector('#AddToCartText');
          if (label) label.textContent = form.getAttribute('id') === `product-form-sticky-${this.dataset.productId}` ? window.variantStrings.addTobag : window.variantStrings.addToCart;
          const parent = button.closest('.product-form__item--submit');
          if (parent) { parent.classList.remove('soldout'); parent.removeAttribute('data-toggle'); parent.removeAttribute('data-target'); }
          form.querySelector('.product-form__item--checkout')?.removeAttribute('disabled');
        }
      }
      const checkout = form.querySelector('[data-commercial-dynamic-checkout], .product-form__item--checkout');
      if (checkout) { checkout.hidden = !allowed; checkout.inert = !allowed; checkout.setAttribute('aria-hidden', String(!allowed)); }
    });
  }

  commitSelectionResponse(text, sectionId, requestId) {
    const currency = this.selectedDisplayCurrency();
    const rows = this.selectionForms().map(form => {
      const sticky = form.getAttribute('id') === `product-form-sticky-${this.dataset.productId}`;
      const source = this.validatePriceResponse(text, sectionId, requestId, sticky);
      const node = this.ownedPrice(`${sticky ? 'ProductPriceStick' : 'ProductPrice'}-${this.dataset.productId}`);
      if (!node) throw new Error('OWNED_PRICE_DESTINATION_MISSING');
      return { node, baseHtml: source.innerHTML, html: this.displayPrice(source.innerHTML, currency) };
    });
    if (!rows.length) throw new Error('OWNED_PRICE_EMPTY');
    const mediaId = this.currentVariant.featured_media?.id;
    const media = mediaId ? this.featuredMedia(mediaId) : null;
    if (mediaId && !media) throw new Error('VARIANT_MEDIA_MISSING');
    // All owned prices and media must be provable before any purchase ID is restored.
    const version = this.variantRequestVersion;
    const complete = () => {
      if (this.variantRequestVersion !== version || this.currentVariant?.id !== requestId) return;
      if (media && !media.classList.contains('act')) throw new Error('VARIANT_MEDIA_NOT_ACTIVE');
      rows.forEach(row => { row.node.innerHTML = row.html; });
      this.selectionPeers().forEach(peer => { peer.priceCommit = rows; peer.renderedVersion = version; });
      this.updateVariantInput();
      this.settleScopedPurchase();
      const sku = this.scopeId('variantSku');
      if (sku) sku.textContent = this.currentVariant.sku || '';
    };
    const pendingMedia = this.updateMedia();
    if (pendingMedia) return pendingMedia.then(complete);
    complete();
  }

  validatePriceResponse(text, sectionId, requestId, sticky) {
    const html = new DOMParser().parseFromString(text, 'text/html');
    const sections = Array.from(html.querySelectorAll('[id]')).filter(node => node.id === `shopify-section-${sectionId}`);
    if (sections.length !== 1) throw new Error('SECTION_RESPONSE_INVALID');
    const section = sections[0];
    const forms = Array.from(section.querySelectorAll('form')).filter(form => form.getAttribute('id') === `product-form-${sectionId}`);
    if (forms.length !== 1 || forms[0].querySelector('input[name="id"]')?.getAttribute('value') !== String(requestId)) throw new Error('VARIANT_RESPONSE_INVALID');
    const data = forms[0].querySelector('product-variant-swatch script[type="application/json"], product-variant-dropdown script[type="application/json"]');
    const variants = JSON.parse(data?.textContent || 'null');
    const variant = Array.isArray(variants) && variants.find(row => row.id === requestId);
    if (!variant || variant.price !== this.currentVariant.price || JSON.stringify(variant.options) !== JSON.stringify(this.currentVariant.options)) throw new Error('VARIANT_FACT_RESPONSE_INVALID');
    const priceId = `${sticky ? 'ProductPriceStick' : 'ProductPrice'}-${sectionId}`;
    const prices = Array.from(section.querySelectorAll('[id]')).filter(node => node.id === priceId);
    const price = this.priceContainer(prices);
    if (!price || !price.textContent.trim()) throw new Error('PRICE_RESPONSE_INVALID');
    const money = price.querySelector('span.money[itemprop="price"]');
    if (money && (!money.hasAttribute('content') || !money.getAttribute('content').trim() || Math.round(Number(money.getAttribute('content')) * 100) !== variant.price)) throw new Error('PRICE_AMOUNT_RESPONSE_INVALID');
    return price;
  }

  selectionScope() {
    return this.closest('#content_quickview') || document;
  }

  selectionPeers() {
    const scope = this.selectionScope();
    return Array.from(scope.querySelectorAll('product-variant-dropdown, product-variant-swatch, product-variant-selects-stick'))
      .filter(peer => peer.dataset.productId === this.dataset.productId && peer.selectionScope() === scope);
  }

  selectionForms() {
    const scope = this.selectionScope();
    const ids = [`product-form-${this.dataset.productId}`, `product-form-sticky-${this.dataset.productId}`];
    return Array.from(scope.querySelectorAll('form')).filter(form => ids.includes(form.getAttribute('id')) && (form.closest('#content_quickview') || document) === scope);
  }

  synchronizeSelection() {
    const peers = this.selectionPeers();
    const version = Math.max(0, ...peers.map(peer => peer.variantRequestVersion || 0)) + 1;
    peers.forEach(peer => {
      peer.variantRequestVersion = version;
      peer.renderedVersion = null;
      peer.options = this.options.slice();
      if (peer !== this) {
        const fields = Array.from(peer.querySelectorAll('fieldset'));
        if (fields.length) fields.forEach((field, index) => field.querySelectorAll('input[type="radio"]').forEach(input => { input.checked = input.value === this.options[index]; }));
        else peer.querySelectorAll('select').forEach((select, index) => { select.value = this.options[index]; });
      }
      peer.updateMasterId();
    });
    if (!this.currentVariant || peers.some(peer => !peer.currentVariant || peer.currentVariant.id !== this.currentVariant.id)) peers.forEach(peer => { peer.currentVariant = undefined; });
    this.updateVariantInput();
  }

  updateVariantInput() {
    this.selectionForms().forEach(form => {
      const input = form.querySelector('input[name="id"]');
      if (!input) return;
      const ready = this.currentVariant && this.renderedVersion === this.variantRequestVersion;
      input.value = ready ? this.currentVariant.id : '';
      input.disabled = !ready;
      if (!ready) {
        const button = form.querySelector('[name="add"]');
        if (button) { button.disabled = true; button.setAttribute('aria-disabled', 'true'); }
        const checkout = form.querySelector('[data-commercial-dynamic-checkout], .product-form__item--checkout');
        if (checkout) { checkout.hidden = true; checkout.inert = true; checkout.setAttribute('aria-hidden', 'true'); }
      }
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  responseIsCurrent(version, id) {
    return this.selectionPeers().every(peer => {
      peer.updateOptions();
      return peer.variantRequestVersion === version && peer.currentVariant?.id === id && peer.currentVariant.options.every((option, index) => peer.options[index] === option);
    }) && this.selectionForms().every(form => { const input = form.querySelector('input[name="id"]'); return input && (this.renderedVersion === version ? (!input.disabled && input.value === String(id)) : (input.disabled && input.value === '')); });
  }

  failSelectionRequest(version) {
    if (this.variantRequestVersion !== version) return;
    this.selectionPeers().forEach(peer => { peer.variantRequestVersion = version + 1; peer.currentVariant = undefined; });
    this.updateVariantInput();
  }

  updateVariantStatuses() {
    const selectedOptionOneVariants = this.variantData.filter(variant => this.querySelector(':checked').value === variant.option1);
    const inputWrappers = [...this.querySelectorAll('fieldset')];
    inputWrappers.forEach((option, index) => {
      if (index === 0) return;
      const optionInputs = [...option.querySelectorAll('input[type="radio"], option')]
      const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked').value;
      const availableOptionInputsValue = selectedOptionOneVariants.filter(variant => variant.available && variant[`option${ index }`] === previousOptionSelected).map(variantOption => variantOption[`option${ index + 1 }`]);
      this.setInputAvailability(optionInputs, availableOptionInputsValue)
    });
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach(input => {
      if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
        input.innerText = input.getAttribute('value');
      } else {
        input.innerText = window.variantStrings.unavailable_with_option.replace('[value]', input.getAttribute('value'));
      }
    });
  }

  updatePickupAvailability() {
    const pickUpAvailability = this.scopeOne('pickup-availability');
    if (!pickUpAvailability) return;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute('available');
      pickUpAvailability.innerHTML = '';
    }
  }

  renderProductInfo() {
    const requestVersion = this.variantRequestVersion;
    const requestId = this.currentVariant.id;
    const requestedVariantId = this.currentVariant.id;
    const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.productId;
    return fetch(`${this.dataset.productUrl}?variant=${requestedVariantId}&section_id=${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.productId}`)
      .then((response) => { if (!response.ok || response.status < 200 || response.status >= 300) throw new Error('HTTP_RESPONSE_INVALID'); return response.text(); })
      .then((responseText) => {
        if (!this.responseIsCurrent(requestVersion, requestId)) return;
        return this.commitSelectionResponse(responseText, sectionId, requestId);
      }).catch(() => this.failSelectionRequest(requestVersion));
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const productForm = this.scopeId(`product-form-${this.dataset.productId}`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > #AddToCartText');
    const addButtonParent = productForm.querySelector('.product-form__item--submit');
    const addButton2 = productForm.querySelector('.product-form__item--checkout');
    if (!addButton) return;
    addButton.disabled = disable;

    if (disable) {
      if (text) addButtonParent.setAttribute('data-toggle', 'modal');
      if (text) addButtonParent.setAttribute('data-target', '#Form_newletter');
      if (text) addButtonParent.classList.add('soldout');
      if (text) addButtonText.textContent = text;
      if (!addButton2) return;
      addButton2.setAttribute('disabled', true);
    } else {
      const variant_id = this.currentVariant.id;
      const inventoryPolicy = this.scopeId("productSelect").querySelector('[value="'+variant_id+'"]').getAttribute('data-inventory_policy');
      const inventoryManagement = this.scopeId("productSelect").querySelector('[value="'+variant_id+'"]').getAttribute('data-inventory_management');
      if(inventoryPolicy == 'continue' && inventoryManagement == 'shopify' ) {
        addButtonText.textContent = window.variantStrings.preorder;
      } else {
        addButtonText.textContent = window.variantStrings.addToCart;
      }
      addButtonParent.classList.remove('soldout');
      addButtonParent.removeAttribute('data-toggle');
      addButtonParent.removeAttribute('data-target');
      if (!addButton2) return;
      addButton2.removeAttribute('disabled');
    }
    if (!modifyClass) return;
  }
  setUnavailable() {
    const addButton = this.scopeId(`product-form-${this.dataset.productId}`)?.querySelector('[name="add"]');
    if (!addButton) return;
    const addButtonText = addButton.querySelector('#AddToCartText');
    addButtonText.textContent = window.variantStrings.unavailable;
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}
customElements.define('product-variant-dropdown', ProductVariantDropdown);

class ProductVariantSwatch extends ProductVariantDropdown {
  constructor() {
    super();
  }
  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach(input => {
      if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
        input.classList.remove('disabled');
      } else {
        input.classList.add('disabled');
      }
    });
  }
  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
  }
}

customElements.define('product-variant-swatch', ProductVariantSwatch);

class ProductVariantSelectsStick extends ProductVariantDropdown {
  constructor() {
    super();
  }
  renderProductInfo() {
    const requestVersion = this.variantRequestVersion;
    const requestId = this.currentVariant.id;
    const sectionId = this.dataset.originalSection || this.dataset.productId;
    return fetch(`${this.dataset.productUrl}?variant=${this.currentVariant.id}&section_id=${this.dataset.productId}`)
      .then((response) => { if (!response.ok || response.status < 200 || response.status >= 300) throw new Error('HTTP_RESPONSE_INVALID'); return response.text(); })
      .then((responseText) => {
        if (!this.responseIsCurrent(requestVersion, requestId)) return;
        return this.commitSelectionResponse(responseText, sectionId, requestId);
      }).catch(() => this.failSelectionRequest(requestVersion));
  }
  toggleAddButton(disable = true, text, modifyClass = true) {
    const productForm = this.scopeId(`product-form-sticky-${this.dataset.productId}`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > #AddToCartText');
    const addButtonParent = productForm.querySelector('.product-form__item--submit');
    const addButton2 = productForm.querySelector('.product-form__item--checkout');
    if (!addButton) return;
    addButton.disabled = disable;

    if (disable) {
      if (text) addButtonParent.setAttribute('data-toggle', 'modal');
      if (text) addButtonParent.setAttribute('data-target', '#Form_newletter');
      if (text) addButtonParent.classList.add('soldout');
      if (text) addButtonText.textContent = text;
      if (!addButton2) return;
      addButton2.setAttribute('disabled', true);
    } else {
      const variant_id = this.currentVariant.id;
      const inventoryPolicy = this.scopeId("productSelect").querySelector('[value="'+variant_id+'"]').getAttribute('data-inventory_policy');
      const inventoryManagement = this.scopeId("productSelect").querySelector('[value="'+variant_id+'"]').getAttribute('data-inventory_management');
      if(inventoryPolicy == 'continue' && inventoryManagement == 'shopify' ) {
        addButtonText.textContent = window.variantStrings.preorder;
      } else {
        addButtonText.textContent = window.variantStrings.addTobag;
      }
      addButtonParent.classList.remove('soldout');
      addButtonParent.removeAttribute('data-toggle');
      addButtonParent.removeAttribute('data-target');
      if (!addButton2) return;
      addButton2.removeAttribute('disabled');
    }
    if (!modifyClass) return;
  }
  setUnavailable() {
    const addButton = this.scopeId(`product-form-sticky-${this.dataset.productId}`)?.querySelector('[name="add"]');
    if (!addButton) return;
    const addButtonText = addButton.querySelector('#AddToCartText');
    addButtonText.textContent = window.variantStrings.unavailable;
  }
}

customElements.define('product-variant-selects-stick', ProductVariantSelectsStick);
